import type { HairlineProps } from "@lucasmarkes/hairline/react";
import { Component, type ComponentType, type CSSProperties, type ReactNode } from "react";

import { cn } from "~/lib/utils";

/**
 * The plates are filled, so they must be painted with the colour of the surface the figure sits on.
 * A container whose own surface differs from the page (a sheet) sets `--hairline-surface`.
 */
const PLATE = {
  background: "var(--hairline-surface, var(--background))",
  card: "var(--card)",
} as const;

export type HairlineSurface = keyof typeof PLATE;

type HairlineArtProps = Omit<HairlineProps, "style" | "className"> & {
  figure: ComponentType<HairlineProps>;
  surface?: HairlineSurface;
  className?: string;
};

/** A figure that fails to mount draws nothing: decoration is never worth an error screen. */
class DrawNothingOnError extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override render() {
    return this.state.failed ? null : this.props.children;
  }
}

/**
 * A Hairline figure drawn in the app's own ink. Decorative unless given a
 * `label`, since the text beside it already says what the screen is about.
 */
export function HairlineArt({ figure: Figure, surface = "background", className, ...props }: HairlineArtProps) {
  const plate = PLATE[surface];
  const style = {
    "--hairline-plate": plate,
    "--hairline-hi": "var(--foreground)",
    "--hairline-edge": `color-mix(in oklab, var(--foreground) 78%, ${plate})`,
    "--hairline-mid": "var(--muted-foreground)",
    "--hairline-lo": `color-mix(in oklab, var(--muted-foreground) 50%, ${plate})`,
  } as CSSProperties;
  return (
    <div
      {...(props.label || props["aria-label"] || props["aria-labelledby"] ? {} : { "aria-hidden": true })}
      data-slot="hairline-art"
      className={cn("aspect-[5/4] max-w-full shrink-0 [--hairline-stroke:1.1]", className)}
      style={style}
    >
      <DrawNothingOnError>
        <Figure {...props} className="w-full" />
      </DrawNothingOnError>
    </div>
  );
}
